<template>
  <div class="page t-container">
    <span class="page-subheading t-text-xs t-text-gray-600 t-font-medium">
      HOME | BOOKINGS | <span v-if="!isLoading">{{ consignment.number }}</span>
    </span>

    <div v-if="isLoading" class="text-center mb-4">
      <i class="fa fa-spinner fa-spin fa-2x"></i>
    </div>

    <base-booking-details-page
      v-if="!isLoading"
      :booking="consignment"
      :map-data="mapData"
      :map-movement-timeline="mapMovementTimeline"
    >
      <template v-slot:card-header-side>
        <div class="t-relative" v-click-outside="onStatusOptionsClickOutside">
          <button
            @click="onShowStatusOptionsClick"
            class="
              t-flex
              t-items-center
              t-py-2
              t-px-4
              t-bg-orange
              t-rounded
              t-text-white
              t-font-semibold
            "
            :class="[
              {
                't-bg-orange': consignment.status_human === 'Booked',
                't-bg-dodger-blue': consignment.status_human === 'In Transit',
                't-bg-green-600': consignment.status_human === 'Delivered'
              }
            ]"
          >
            {{ consignment.status_human }}
            <svg
              v-if="consignment.status_human !== 'Delivered'"
              xmlns="http://www.w3.org/2000/svg"
              class="t-ml-2 t-h-5 t-w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>

          <div
            v-if="showStatusOptions"
            class="
              t-absolute
              t-mt-1
              t-shadow
              t-border
              t-border-gray-400
              t-rounded
              t-w-32
              t-bg-white
            "
          >
            <a
              v-if="consignment.status_human === 'Booked'"
              @click="onStatusChangeClick('In Transit', consignmentId)"
              class="t-block t-px-4 t-py-2 hover:t-bg-gray-200"
            >
              In Transit
            </a>
            <a
              v-if="
                consignment.status_human === 'Booked' ||
                  consignment.status_human === 'In Transit'
              "
              @click="onStatusChangeClick('Delivered', consignmentId)"
              class="t-block t-px-4 t-py-2 hover:t-bg-gray-200"
            >
              Delivered
            </a>
          </div>

          <div
            :id="'date_time_' + consignment.id"
            class="date-time"
            style="
              position: absolute;
              top: 40px;
              left: 12px;
              background: #fff;
              border: 1px solid #ccc;
              padding: 10px;
              z-index: 10000;
              visibility: hidden;
            "
          >
            <h2 style="margin-bottom: 10px">
              Select Date and Time
              {{
                consignment.status_human === "Booked"
                  ? "Picked up"
                  : "Delivered"
              }}
            </h2>
            <div class="t-flex">
              <v-date-picker v-model="statusDate" mode="dateTime" />
              <div style="flex-direction: column; padding-left: 10px">
                <button
                  class="btn btn-primary"
                  style="width: 100%; margin-bottom: 10px"
                  @click="updateStatusWithDate(consignment.id)"
                >
                  Update
                </button>
                <button
                  class="btn btn-danger"
                  style="width: 100%; margin-bottom: 10px"
                  @click="closeDateTimePickers()"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:card-side-right>
        <div
          class="
            t-flex t-flex-col t-space-y-3 t-space-x-2 t-text-md
            md:t-block md:t-space-y-2 md:t-text-sm md:t-py-2
          "
        >
          <Dropdown
            class="t-inline-block"
            v-if="consignment.carrier.name !== 'Aramex'"
          >
            <Button type="primary" size="large">
              Download Files
              <Icon type="ios-arrow-down" />
            </Button>
            <DropdownMenu slot="list">
              <DropdownItem v-if="consignment.purchase_order">
                <a :href="consignment.id + '/purchase_order'">Purchase Order</a>
              </DropdownItem>
              <DropdownItem v-if="consignment.pod">
                <a :href="consignment.id + '/proof-of-delivery'"
                  >Proof Of Delivery</a
                >
              </DropdownItem>
              <DropdownItem
                v-if="!consignment.pod && !consignment.purchase_order"
              >
                No files
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>

          <Upload
            v-if="!consignment.pod && consignment.status === 40"
            class="t-inline-block"
            :action="consignment.id + '/pod'"
            :on-success="handlePodUpload"
            :on-error="handlePodUpload"
            :before-upload="beforePodUpload"
          >
            <Button icon="ios-cloud-upload-outline">Upload POD file</Button>
          </Upload>
        </div>
      </template>
    </base-booking-details-page>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import "moment-timezone";

import BaseBookingDetailsPage from "./BaseBookingDetailsPage";

export default {
  name: "CarrierBookingDetailsPage",
  components: {
    BaseBookingDetailsPage
  },
  props: {
    consignmentId: {}
  },
  data() {
    return {
      isLoading: true,
      consignment: null,
      mapData: {
        rawMapMovements: {
          data: []
        },
        pickupLocation: {
          lat: null,
          lng: null
        },
        deliveryLocation: {
          lat: null,
          lng: null
        },
        latestPoint: {
          lat: null,
          lng: null
        }
      },
      mapMovementTimeline: null,
      showStatusOptions: false,
      statusDate: moment().format("YYYY-MM-DD HH:mm"),
      podFile: null
    };
  },
  created() {
    this.isLoading = true;
    this.fetchConsignment().then(consignment => {
      this.consignment = consignment;

      // Changing status names
      if (this.consignment.status_human === "Assigned") {
        this.consignment.status_human = "Booked";
      } else if (this.consignment.status_human === "Picked up") {
        this.consignment.status_human = "In Transit";
      }

      this.consignment.pickup_after = moment(
        this.consignment.pickup_after
      ).format("hh:mm a ddd Do MMM");
      this.consignment.deliver_before = moment(
        this.consignment.deliver_before
      ).format("hh:mm a ddd Do MMM");

      this.processMapData(
        this.consignment.pickup_location,
        this.consignment.delivery_location
      );
      this.processFreightEvents(
        this.consignment.freight_events,
        this.consignment
      );
      this.isLoading = false;
    });
  },
  methods: {
    /**
     * Fetches a carrier consignment. A consignment is a leg.
     *
     * @returns {Promise<*>}
     */
    async fetchConsignment() {
      try {
        let res = await axios.get(`?t=${Date.now()}`);
        return res.data.data;
      } catch (e) {
        this.$Message.error({
          content:
            "Sorry, something is not working correctly. Please contact with Freighthub to get booking details.",
          duration: 10,
          closable: true
        });
        Event.$emit("serverError");
      }
    },

    processMapData(pickupLocation, deliveryLocation) {
      this.mapData.pickupLocation.lat = Number.parseFloat(
        pickupLocation.address.lat
      );
      this.mapData.pickupLocation.lng = Number.parseFloat(
        pickupLocation.address.lng
      );
      this.mapData.deliveryLocation.lat = Number.parseFloat(
        deliveryLocation.address.lat
      );
      this.mapData.deliveryLocation.lng = Number.parseFloat(
        deliveryLocation.address.lng
      );

      this.mapData.rawMapMovements.data = this.consignment.freight_events;
    },

    processFreightEvents(events, consignment) {
      let movementTimeline = [];
      let pickedUp = false;

      if (events) {
        // Push the placed leg if no placed type of freight event
        const placedTypeFreightEvent = events.find(
          movement => movement.type_id === 1
        );
        if (!placedTypeFreightEvent) {
          movementTimeline.push({
            day: moment
              .utc(consignment.placed_at.toString(), "hh:mm a ddd Do MMM")
              .local()
              .format("ddd Do MMM"),
            time: moment
              .utc(consignment.placed_at.toString(), "hh:mm a ddd Do MMM")
              .local()
              .format("hh:mm a"),
            content: "Booking placed",
            required: true
          });
        }

        let transitStopName = "";
        events.forEach(event => {
          const movementTimelineItem = {
            time: moment
              .utc(event.time.toString(), "hh:mm a ddd Do MMM")
              .tz("Pacific/Auckland")
              .format("ddd Do MMM hh:mm a"),
            content: "",
            typeId: event.type_id,
            required: false,
            trackerId: ""
          };

          if (event.type_id === 1) {
            movementTimelineItem.content = "Booking placed";
            movementTimelineItem.required = true;
          } else if (event.type_id === 2) {
            movementTimelineItem.content =
              "Picked up from " + consignment.pickup_location.name;
            movementTimelineItem.required = true;
            pickedUp = true;
          } else if (event.type_id === 3) {
            movementTimelineItem.content =
              "Delivered to " + consignment.delivery_location.name;
            movementTimelineItem.required = true;
          } else if (event.type_id === 4) {
            movementTimelineItem.content = "POD uploaded";
          } else if (event.type_id === 11) {
            movementTimelineItem.content =
              "Allocated to carrier [" +
              (consignment.carrier.name === null
                ? "unknown"
                : consignment.carrier.name) +
              "]";
          } else if (event.type_id === 12) {
            movementTimelineItem.content =
              "Allocated to truck [" +
              (event.truck_plate === null || event.truck_plate === ""
                ? "unknown"
                : event.truck_plate) +
              "]";
          } else if (event.type_id === 13) {
            const depotName = event.name ? event.name : "Depot";
            movementTimelineItem.content = "Uplifting at " + depotName;
          } else if (event.type_id === 14) {
            const depotName = event.name ? event.name : "Depot";
            movementTimelineItem.content = "Uplifted at " + depotName;
          } else if (event.type_id === 15) {
            const depotName = event.name ? event.name : "Depot";
            movementTimelineItem.content = "Offloading at " + depotName;
          } else if (event.type_id === 16) {
            const depotName = event.name ? event.name : "Depot";
            movementTimelineItem.content = "Offloaded at " + depotName;
          } else if (event.type_id === 17) {
            movementTimelineItem.content = "Manifested for next leg";
          } else if (event.type_id === 18) {
            let trackerId = event.eroad_device_id
              ? event.eroad_device_id.toString().substring(4) + " "
              : "";
            movementTimelineItem.content =
              "Tracker " + trackerId + "Linked To Booking";
            movementTimelineItem.required = true;
            movementTimelineItem.eroadWhereDeviceId = trackerId;
          } else if (event.type_id === 19) {
            movementTimelineItem.content =
              "Arrived at transit stop at " + event.name;
            transitStopName = event.name;
          } else if (event.type_id === 20) {
            movementTimelineItem.content =
              "Left transit stop at " + transitStopName;
          } else if (event.type_id === 21) {
            let trackerId = event.eroad_device_id
              ? event.eroad_device_id.toString().substring(4) + " "
              : "";
            movementTimelineItem.content =
              "Tracker " + trackerId + "Unlinked From Booking";
            movementTimelineItem.required = true;
            movementTimelineItem.eroadWhereDeviceId = trackerId;
          } else if (event.type_id === 22) {
            // TYPE_DELIVERY_ESTIMATE
            movementTimelineItem.content = "Delivery Estimate";
          } else if (event.type_id === 99) {
            movementTimelineItem.content = "Unknown";
          }

          // Prevent duplicates that have been inserted in freight_events by the tracker
          if (
            !movementTimeline.find(t => {
              return (
                t.time === movementTimelineItem.time &&
                t.content === movementTimelineItem.content
              );
            }) &&
            event.type_id !== 99 &&
            event.type_id !== null &&
            !(event.type_id === 13 && event.name === "T.B.A") &&
            !(event.type_id === 9 && event.eroad_device_id == null) &&
            !((event.type_id === 8 || event.type_id === 9) && !pickedUp)
          ) {
            movementTimeline.push(movementTimelineItem);
          }
        });

        this.mapMovementTimeline = movementTimeline;
      }
    },
    beforePodUpload(file) {
      this.podFile = file;
    },
    handlePodUpload() {
      let formData = new FormData();
      formData.append("delivery_file", this.podFile);
      formData.append("_method", "POST");
      axios
        .post(this.consignment.id + "/pod", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then(() => {
          this.consignment.has_pod = true;
          this.$Message.success("POD uploaded successfully.");
        })
        .catch(() => {
          this.$Message.error("Failed to upload POD.");
        });
    },

    onShowStatusOptionsClick() {
      if (this.consignment.status_human === "Delivered") {
        return;
      }
      this.showStatusOptions = true;
    },
    onStatusOptionsClickOutside() {
      this.showStatusOptions = false;
    },
    onStatusChangeClick(status, id) {
      this.newStatus = status;
      this.closeDateTimePickers();

      document.getElementById("date_time_" + id).style.visibility = "visible";
    },
    updateStatusWithDate(consignmentId) {
      this.closeDateTimePickers();

      switch (this.newStatus) {
        case "In Transit":
          this.updatePickedUp(consignmentId);
          break;
        case "Delivered":
          this.updateDelivered(consignmentId);
          break;
        case "Cancel":
          break;
        default:
          break;
      }
    },
    updatePickedUp(id) {
      let dateTime = moment(this.statusDate, "YYYY-MM-DD HH:mm");
      let dateTimeString = dateTime.format("ddd D MMM gggg h A");

      axios
        .put("/carry-freight/bookings/" + id + "/pickup", {
          update_time: dateTimeString
        })
        .then(response => {
          let data = response.data;

          if (data.is_success) {
            this.$swal({
              title: data.message,
              type: "success"
            });

            location.reload();
          } else {
            this.$swal({
              title: data.message,
              type: "error"
            });
          }
        })
        .catch(() => {
          Event.$emit("serverError");
        });
    },
    updateDelivered(id) {
      let dateTime = moment(this.statusDate, "YYYY-MM-DD HH:mm");
      let dateTimeString = dateTime.format("ddd D MMM gggg h A");

      axios
        .put("/carry-freight/bookings/" + id + "/delivered", {
          update_time: dateTimeString
        })
        .then(response => {
          let data = response.data;

          if (data.is_success) {
            this.$swal({
              title: data.message,
              type: "success"
            });

            location.reload();
          } else {
            this.$swal({
              title: data.message,
              type: "error"
            });
          }
        })
        .catch(() => {
          Event.$emit("serverError");
        });
    },
    closeDateTimePickers() {
      let list, index;
      list = document.getElementsByClassName("date-time");
      for (index = 0; index < list.length; ++index) {
        document.getElementById(list[index].id).style.visibility = "hidden";
      }
    }
  }
};
</script>
